import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";
import api from "../lib/api";
import useSeo from "../hooks/useSeo";
import { ChevronRight, Calculator, Check, AlertTriangle } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import QuoteWizard from "../components/QuoteWizard";

export default function CalculatorPage() {
  useSeo({
    title: "Ekipman Hesaplama Aracı — Otel Pro",
    description: "Tesis türünüze, yıldız hedefinize ve oda dağılımınıza göre komple otel ekipman listesi hesaplayın.",
  });

  const [form, setForm] = useState({
    facility_type: "Otel",
    star_target: 4,
    standard_rooms: 20,
    suite_rooms: 2,
    deluxe_rooms: 5,
    accessible_rooms: 1,
    meeting_halls: 1,
    restaurant_capacity: 80,
    has_spa: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k, v) => update(k, Number(v) || 0);

  const calculate = async () => {
    setLoading(true);
    try {
      const r = await api.post("/calculator/compute", form);
      setResult(r.data);
      setTimeout(() => document.getElementById("calc-result")?.scrollIntoView({ behavior: "smooth" }), 100);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const items = (result?.items || []).map((i) => ({ name: i.name, quantity: i.quantity, notes: i.note }));

  return (
    <Layout>
      <div className="bg-softbg">
        <div className="container-site py-5 flex items-center gap-2 text-xs text-gray-500">
          <Link to="/" className="hover:text-navy">Ana Sayfa</Link>
          <ChevronRight size={12} />
          <span className="text-navy font-medium">Ekipman Hesaplama</span>
        </div>
      </div>

      <section className="bg-navy text-white py-20">
        <div className="container-site max-w-3xl">
          <div className="flex items-center gap-3 mb-3">
            <Calculator className="text-gold" size={28} />
            <p className="accent-script text-gold text-2xl">Benzersiz Araç</p>
          </div>
          <h1 className="heading-serif text-3xl md:text-5xl font-semibold">Otel Ekipman Hesaplama</h1>
          <p className="text-white/75 mt-4">
            Oteliniz için tüm ekipman ihtiyacınızı dakikalar içinde çıkarın. Yıldız uyumluluk kontrolü dahil.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-site max-w-4xl">
          <div className="card-premium p-7" data-testid="calc-form">
            <h2 className="heading-serif text-xl font-semibold text-navy mb-5">Tesis Bilgileri</h2>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div>
                <Label className="text-xs mb-1 block">Tesis Türü</Label>
                <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={form.facility_type} onChange={(e) => update("facility_type", e.target.value)} data-testid="calc-facility">
                  <option>Otel</option><option>Butik Otel</option><option>Resort</option><option>Apart</option><option>Pansiyon</option>
                </select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Yıldız Hedefi</Label>
                <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={form.star_target} onChange={(e) => num("star_target", e.target.value)} data-testid="calc-star">
                  {[1,2,3,4,5].map((s) => <option key={s} value={s}>{s} Yıldız</option>)}
                </select>
              </div>
              <div className="flex items-center gap-3 pt-6">
                <Switch checked={form.has_spa} onCheckedChange={(v) => update("has_spa", v)} data-testid="calc-spa" />
                <Label className="text-sm">Spa & Wellness Alanı</Label>
              </div>
            </div>

            <h3 className="heading-serif text-lg font-semibold text-navy mb-4">Oda Dağılımı</h3>
            <div className="grid md:grid-cols-3 gap-4 mb-4">
              <NumberField label="Standart Oda" value={form.standard_rooms} onChange={(v) => num("standard_rooms", v)} testid="calc-std" />
              <NumberField label="Suite Oda" value={form.suite_rooms} onChange={(v) => num("suite_rooms", v)} testid="calc-suite" />
              <NumberField label="Deluxe Oda" value={form.deluxe_rooms} onChange={(v) => num("deluxe_rooms", v)} testid="calc-deluxe" />
              <NumberField label="Engelli Oda" value={form.accessible_rooms} onChange={(v) => num("accessible_rooms", v)} testid="calc-acc" />
              <NumberField label="Toplantı Salonu" value={form.meeting_halls} onChange={(v) => num("meeting_halls", v)} testid="calc-meeting" />
              <NumberField label="Restoran Kapasitesi (kişi)" value={form.restaurant_capacity} onChange={(v) => num("restaurant_capacity", v)} testid="calc-rest" />
            </div>

            <Button onClick={calculate} disabled={loading} className="bg-gold hover:bg-[#c49d58] w-full md:w-auto" data-testid="calc-compute">
              {loading ? "Hesaplanıyor..." : "Ekipman Listemi Hesapla"}
            </Button>
          </div>

          {result && (
            <div id="calc-result" className="mt-10" data-testid="calc-result">
              <div className="flex items-end justify-between mb-4">
                <div>
                  <p className="accent-script text-gold text-xl">Sonuç</p>
                  <h2 className="heading-serif text-2xl font-semibold text-navy">Hesaplanan Ekipman Listesi</h2>
                  <p className="text-sm text-gray-500 mt-1">Toplam kalem: {result.items.length} · Toplam adet: {result.total_items}</p>
                </div>
              </div>

              {result.compliance?.warnings?.length > 0 && (
                <div className="mb-5 p-4 rounded-lg bg-amber-50 border border-amber-200 flex gap-3">
                  <AlertTriangle className="text-amber-600 shrink-0" size={18} />
                  <div>
                    <div className="font-medium text-amber-900 text-sm">Yıldız Uyumluluk Uyarıları</div>
                    <ul className="text-xs text-amber-800 mt-1 space-y-1 list-disc list-inside">
                      {result.compliance.warnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              <div className="rounded-xl overflow-hidden border border-gray-100 bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-navy text-white">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium">Ekipman</th>
                      <th className="text-left px-4 py-3 font-medium">Kategori</th>
                      <th className="text-center px-4 py-3 font-medium">Adet</th>
                      <th className="text-center px-4 py-3 font-medium">Durum</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.items.map((it, i) => (
                      <tr key={i} className={i % 2 ? "bg-softbg" : "bg-white"}>
                        <td className="px-4 py-3">
                          <div className="font-medium text-navy">{it.name}</div>
                          {it.note && <div className="text-[11px] text-gray-500 mt-0.5">{it.note}</div>}
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{it.category}</td>
                        <td className="px-4 py-3 text-center font-semibold text-navy">{it.quantity}</td>
                        <td className="px-4 py-3 text-center">
                          {it.required ? (
                            <span className="inline-flex items-center gap-1 text-rose-600 text-xs"><Check size={12} /> Zorunlu</span>
                          ) : (
                            <span className="text-gray-400 text-xs">Opsiyonel</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={() => setWizardOpen(true)} className="bg-gold hover:bg-[#c49d58]" data-testid="calc-send-quote">
                  Bu Listeyi Komple Teklif Al
                </Button>
                <Button
                  variant="outline"
                  onClick={async () => {
                    try {
                      const r = await api.post("/calculator/pdf", form, { responseType: "blob" });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url;
                      a.download = `otel-pro-hesaplama-${form.star_target}yildiz.pdf`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { /* ignored */ }
                  }}
                  data-testid="calc-download-pdf"
                >
                  PDF İndir
                </Button>
                <Button variant="outline" onClick={() => window.print()} data-testid="calc-print">
                  Yazdır
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>

      <QuoteWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialSource="calculator"
        initialItems={items}
        initialNotes={`Hesaplama: ${form.facility_type} · ${form.star_target} yıldız · ${form.standard_rooms + form.suite_rooms + form.deluxe_rooms + form.accessible_rooms} oda`}
      />
    </Layout>
  );
}

const NumberField = ({ label, value, onChange, testid }) => (
  <div>
    <Label className="text-xs mb-1 block">{label}</Label>
    <Input type="number" min={0} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </div>
);
